import { useEffect, useRef, useCallback } from 'react';

/**
 * Canvas particle system with Lissajous orbital paths.
 *
 * Each particle orbits via:
 *   x = cx + Ax * sin(ωx * t + φx)
 *   y = cy + Ay * cos(ωy * t + φy)
 *
 * Draws connection lines between particles closer than `connectionDist`.
 * Returns a ref to attach to a <canvas> element.
 */
export default function useParticleCanvas({
  particleCount,
  connectionDist = 150,
  particleColor = 'rgba(99, 102, 241, 0.65)',        // Electric Indigo
  lineColor = 'rgba(56, 189, 248, 0.15)',             // Ambient Cyan
  particleRadius = 2,
} = {}) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  const getCount = useCallback(() => {
    if (particleCount != null) return particleCount;
    if (typeof window === 'undefined') return 40;
    return window.innerWidth < 768 ? 25 : 55;
  }, [particleCount]);

  // Initialize particles with random Lissajous parameters
  const createParticles = useCallback(
    (w, h) => {
      const count = getCount();
      return Array.from({ length: count }, () => {
        const cx = Math.random() * w;
        const cy = Math.random() * h;
        // Small amplitude so particles stay near their center
        const ax = 30 + Math.random() * 70;
        const ay = 30 + Math.random() * 70;
        // Random integer-ish frequency ratios for Lissajous variety
        const freqChoices = [1, 2, 3, 1.5, 2.5];
        const wx = freqChoices[Math.floor(Math.random() * freqChoices.length)] * (0.3 + Math.random() * 0.4);
        const wy = freqChoices[Math.floor(Math.random() * freqChoices.length)] * (0.3 + Math.random() * 0.4);
        const phaseX = Math.random() * Math.PI * 2;
        const phaseY = Math.random() * Math.PI * 2;
        // Slow linear drift so the whole figure migrates
        const driftX = (Math.random() - 0.5) * 0.15;
        const driftY = (Math.random() - 0.5) * 0.15;
        const radius = particleRadius + Math.random() * 1.5;

        return { cx, cy, ax, ay, wx, wy, phaseX, phaseY, driftX, driftY, radius };
      });
    },
    [getCount, particleRadius]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      const w = parent ? parent.clientWidth : window.innerWidth;
      const h = parent ? parent.clientHeight : window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particlesRef.current = createParticles(w, h);
    };

    resize();
    window.addEventListener('resize', resize);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const draw = () => {
      if (reducedMotion) return; // Skip animation for accessibility

      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);
      ctx.clearRect(0, 0, w, h);

      const dt = 0.008;
      timeRef.current += dt;
      const t = timeRef.current;

      const particles = particlesRef.current;
      const positions = [];

      // Compute positions
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        // Lissajous + linear drift (wrap around edges)
        p.cx += p.driftX;
        p.cy += p.driftY;
        if (p.cx < -50) p.cx = w + 50;
        if (p.cx > w + 50) p.cx = -50;
        if (p.cy < -50) p.cy = h + 50;
        if (p.cy > h + 50) p.cy = -50;

        const x = p.cx + p.ax * Math.sin(p.wx * t + p.phaseX);
        const y = p.cy + p.ay * Math.cos(p.wy * t + p.phaseY);
        positions.push({ x, y, r: p.radius });
      }

      // Draw connections
      ctx.lineWidth = 0.5;
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const dx = positions[i].x - positions[j].x;
          const dy = positions[i].y - positions[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDist) {
            const opacity = 1 - dist / connectionDist;
            ctx.strokeStyle = lineColor.replace(/[\d.]+\)$/, `${(opacity * 0.25).toFixed(3)})`);
            ctx.beginPath();
            ctx.moveTo(positions[i].x, positions[i].y);
            ctx.lineTo(positions[j].x, positions[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const pos of positions) {
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, pos.r, 0, Math.PI * 2);
        ctx.fillStyle = particleColor;
        ctx.fill();

        // Soft glow
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, pos.r * 3, 0, Math.PI * 2);
        ctx.fillStyle = particleColor.replace(/[\d.]+\)$/, '0.08)');
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [createParticles, connectionDist, particleColor, lineColor]);

  return canvasRef;
}
